import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
    ChartConfig,
    ChartContainer,
    ChartLegend,
    ChartLegendContent,
    ChartTooltip,
    ChartTooltipContent,
    ChartTooltipFormatter,
} from "../../ui/chart";
import { ChecklistAnswer } from "../../../types/analytics";

/**
 * Componente customizado para quebrar o texto das perguntas em até três linhas
 * para evitar truncamentos feios no eixo Y.
 */
interface AxisTickProps {
    x?: number;
    y?: number;
    payload?: { value?: string };
}

const CustomYAxisTick = (props: AxisTickProps) => {
    const { x, y, payload } = props;
    const value = payload?.value ?? "";

    const words = value.split(" ");
    const lines: string[] = [];
    let currentLine = "";

    for (const word of words) {
        if ((currentLine + " " + word).length > 38) {
            lines.push(currentLine);
            currentLine = word;
        } else {
            currentLine = currentLine ? `${currentLine} ${word}` : word;
        }
    }
    if (currentLine) {
        lines.push(currentLine);
    }

    const displayLines = lines.slice(0, 3);
    if (lines.length > 3) {
        displayLines[2] = displayLines[2] + "…";
    }

    let dyStart = 4;
    if (displayLines.length === 2) {
        dyStart = -2;
    } else if (displayLines.length === 3) {
        dyStart = -8;
    }

    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={-8}
                y={dyStart}
                textAnchor="end"
                fill="currentColor"
                className="fill-muted-foreground text-[11px]"
            >
                {displayLines.map((line, idx) => (
                    <tspan key={idx} x={-8} dy={idx > 0 ? 12 : 0}>
                        {line}
                    </tspan>
                ))}
            </text>
        </g>
    );
};

export function ChecklistAnswersChart({ data }: { data: ChecklistAnswer[] }) {
    const { t } = useTranslation("gamification");

    const chartData = useMemo(
        () =>
            [...(data ?? [])]
                .sort((a, b) => b.yesPercentage - a.yesPercentage)
                .map((c) => {
                    const total = c.yes + c.no;
                    const qText = (c.questionText || "").trim();
                    return {
                        question: t(`checklists.questions.${qText}` as never, qText),
                        yes: total > 0 ? (c.yes / total) * 100 : 0,
                        no: total > 0 ? (c.no / total) * 100 : 0,
                        rawYes: c.yes,
                        rawNo: c.no,
                        total,
                    };
                }),
        [data, t],
    );

    const config = useMemo<ChartConfig>(
        () => ({
            yes: { label: t("checklists.yes"), color: "hsl(var(--success))" },
            no: { label: t("checklists.no"), color: "hsl(var(--destructive))" },
        }),
        [t],
    );

    const tooltipFormatter = useCallback<ChartTooltipFormatter>(
        (value, name, item) => {
            const rawYes = item.payload?.rawYes ?? 0;
            const rawNo = item.payload?.rawNo ?? 0;
            const total = item.payload?.total ?? 0;
            
            const count = name === "yes" ? rawYes : rawNo;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            return `${count} (${pct}%)`;
        },
        [],
    );

    if (chartData.length === 0) {
        return (
            <p className="text-sm text-muted-foreground">
                {t("checklists.empty")}
            </p>
        );
    }

    return (
        <ChartContainer config={config} className="aspect-auto h-full w-full">
            <BarChart
                data={chartData}
                layout="vertical"
                margin={{ left: 8, right: 16, top: 4, bottom: 4 }}
            >
                <CartesianGrid
                    horizontal={false}
                    strokeDasharray="3 3"
                    className="stroke-border/60"
                />
                <XAxis
                    type="number"
                    domain={[0, 100]}
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(val) => `${Math.round(val)}%`}
                />
                <YAxis
                    type="category"
                    dataKey="question"
                    width={240}
                    interval={0}
                    tickLine={false}
                    axisLine={false}
                    tick={<CustomYAxisTick />}
                />
                <ChartTooltip content={<ChartTooltipContent formatter={tooltipFormatter} />} />
                <ChartLegend content={<ChartLegendContent />} />
                <Bar
                    dataKey="yes"
                    stackId="a"
                    fill="var(--color-yes)"
                    radius={[4, 0, 0, 4]}
                    maxBarSize={26}
                />
                <Bar
                    dataKey="no"
                    stackId="a"
                    fill="var(--color-no)"
                    radius={[0, 4, 4, 0]}
                    maxBarSize={26}
                />
            </BarChart>
        </ChartContainer>
    );
}
